/* eslint-disable @typescript-eslint/no-explicit-any */
import { Either, right, left, fold as foldEither } from "fp-ts/lib/Either";
import { fold, isNone, some, Option } from "fp-ts/lib/Option";
import { pipe } from "fp-ts/lib/pipeable";

import axios, { AxiosResponse, AxiosRequestConfig, CancelToken } from "axios";

import { defaultApiInstance, calculatorApiInstance, CalculatorAppID } from "../utils/DefaultApiInstance";
import { getPruRequestCounter, getPruRequestID } from "../utils/commonFunctions";
import { CalculatorInputs, initialCalculatorInputs, AllocatedStrategy, InvestmentStrategy, TermLength, StrategyList } from "../model/CalculatorInputs";
import { InvestmentDTO } from '../model/investmentDTO';
import { ValuesDTO } from '../model/valuesDTO';
import { getStrategyConfig } from "../model/CalculatorInputsValidation";
import { CalculatorConfig } from "../model/CalculatorConfig";
import { CalculatorInputsDTO } from "../model/CalculatorInputsDTO";
import { AvailableInvestmentStrategies } from "../model/AvailableInvestmentStrategies";
import { CalculatorResponse, ExportResponse, InvestmentScenario, ReportURLComponents } from "../model/CalculatorResponse";



export interface InvestmentRequest {
  investmentIdentifier: string;
  investmentAllocation: number;
  customStartYear: string;
}
export interface CalculatorServiceRequest {

  calculator: {
    productCusip: string;
    premium: {
      premiumPaymentAmount: number;
    };
    investment: Array<InvestmentRequest>;
    user: {
      userIdentifier: string;
    };
    report: {
      preparedBy: string;
      preparedFor: string;
      deliveryEmailAddress: string | undefined;
    } | undefined;
  };
}

export interface CalculatorServiceSecurity {
  nonce: string;
  timestamp: string;
  signature: string;
}

export interface CalculatorServiceSuccessResponse extends CalculatorServiceRequest {
  scenarios: Array<{
    investmentIdentifier: string;
    values: Array<any>;
  }>;
  security: CalculatorServiceSecurity;
}

export interface ExportServiceSuccessResponse extends CalculatorServiceSuccessResponse {
  reportURL: ReportURLComponents;
}

export interface CalculatorServiceFailureResponse {
  responseCode: number;
  errorDetail: {
    subErrorCode: string;
    errorMessage: string;
    rawErrContent: string;
    reportingComponentId: string;
  };
}


export function validateResponseStructure(data: any): boolean {
  return (data.calculator && data.scenarios);
}
export function validateErrorResponseStructure(data: any): boolean {
  return (data.responseCode && data.errorDetail && data.errorDetail.errorMessage);
}


defaultApiInstance.interceptors.request.use(
  (config: any) => {
    config.headers["X-PruRequestCounter"] = getPruRequestCounter();
    config.headers["X-PruRequestID"] = getPruRequestID();
    config.headers["X-UID"] = getPruRequestID();
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);
// TODO: remove duplication
calculatorApiInstance.interceptors.request.use(
  (config: any) => {
    config.headers["X-PruRequestCounter"] = getPruRequestCounter();
    config.headers["X-PruRequestID"] = getPruRequestID();
    config.headers["X-UID"] = getPruRequestID();
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);


const CALCULATOR_UNAVAILABLE_MESSAGE = 'We\'re sorry, our calculator is unavailable at this moment. Please try again later.';
const EXPORT_UNAVAILABLE_MESSAGE = 'We\'re sorry, PDF Export is unavailable at this moment. Please try again later.';
export const EXPORT_CANCELLED_MESSAGE = 'EXPORT_CANCELLED';

function extractErrorMessageAndThrow(responseData: any): void {

  if (validateErrorResponseStructure(responseData)) {
    const errorResponse = responseData as CalculatorServiceFailureResponse;
    if (errorResponse.errorDetail.rawErrContent) {
      let j: any = {};
      try {
        j = JSON.parse(errorResponse.errorDetail.rawErrContent);
      } catch (err) {
        console.log('Calculator API Error: Failed to parse error response: ', errorResponse.errorDetail.rawErrContent);
      }
      if (j.messageText) {
        console.log('Calculator API Error: ', j.messageText);
        throw new Error(j.messageText);
      } else {
        throw new Error(CALCULATOR_UNAVAILABLE_MESSAGE);
      }
    } else {
      console.log('Calculator API Error: ', errorResponse.errorDetail.errorMessage);
      throw new Error(CALCULATOR_UNAVAILABLE_MESSAGE);
    }
  } else {
    console.log("Calculator API Error: Unrecognized response: ", responseData);
    throw new Error(CALCULATOR_UNAVAILABLE_MESSAGE);
  }
}


// TODO: can we remove this - it's only used in a single location 
calculatorApiInstance.interceptors.response.use(
  (response: any) => {
    if (validateResponseStructure(response.data)) {
      return response;
    } else {
      console.log('Calculator API: Response data not recognized: ', response.data);
      extractErrorMessageAndThrow(response);
    }
  },
  (error: any) => {
    //if (axios.isCancel(error)) {}
    console.log(`Calculator API: Error in response: ${error}`);
    if (error.response && error.response?.data && error.response?.data?.responseCode === 400 && error.response?.data?.errorDetail) {
      extractErrorMessageAndThrow(error.response.data);
    } else {
      console.log("Calculator API Error: " + error);
      throw new Error(CALCULATOR_UNAVAILABLE_MESSAGE);
    }
  }
);


export function calculatorInputsToServiceRequest(inputs: CalculatorInputs, config: CalculatorConfig): Either<Error, CalculatorServiceRequest> {
  const failures: Array<string> = [];
  if (isNone(inputs.term)) {
    failures.push("Term length is not set");
  }
  if (isNone(inputs.premiumPayment)) {
    failures.push("Premium amount is not set");
  }
  if (isNone(config.productCusip)) {
    failures.push("Configuration value for productCusip not set")
  }
  const investments: Array<InvestmentRequest> = [];
  const userIdentifier = pipe(config.userIdentifier,
    fold(() => CalculatorAppID,
      (userId) => userId));

  const productCusip = pipe(config.productCusip, fold(() => '', (cusip) => cusip));
  const premiumPaymentAmount = pipe(inputs.premiumPayment, fold(() => 0, (amount) => amount));

  const report = pipe(inputs.exportReport.financialProfessionalName, fold(() => undefined, (preparedBy) =>
    pipe(inputs.exportReport.clientName, fold(() => undefined, (preparedFor) => {
      const deliveryEmailAddress = pipe(inputs.exportReport.clientEmail, fold(() => undefined, (email) => email));
      return {
        preparedBy,
        preparedFor,
        deliveryEmailAddress
      };
    }))));

  pipe(inputs.term,
    fold(() => { return; },
      (termLength) => {
        for (let i = 0; i < inputs.strategyList.getStrategyCount(); i++) {
          const allocatedStrategy = inputs.strategyList.getAllocatedStrategy(i);
          const avail = getStrategyConfig(inputs.term, allocatedStrategy.strategy, config.availableInvestmentStrategies);
          pipe(avail,
            fold(() => {
              failures.push(`Strategy ${i + 1} is not a recognized configuration`);
            },
              (a) => pipe(allocatedStrategy.allocation,
                fold(() => {
                  failures.push(`Strategy ${i + 1} does not have a percentage allocation`);
                },
                  (allocation) => {
                    const dateRange = config.availableDateRanges.getRangeForInvestmentIndexAndTerm(a.index, termLength);
                    const mostRecentStartYear = dateRange.endYear;
                    const customStartYear = pipe(allocatedStrategy.customStartYear, fold(() => mostRecentStartYear, (year) => year));
                    investments.push({ investmentIdentifier: a.productCode, investmentAllocation: allocation, customStartYear: customStartYear.toString() });
                  }))));
        }
      }));



  if (failures.length > 0) {
    return left(new Error(failures.join('; ')));
  } else {
    const serviceRequest: CalculatorServiceRequest = {
      calculator: {
        productCusip,
        premium: {
          premiumPaymentAmount
        },
        investment: investments,
        user: {
          userIdentifier
        },
        report
      }
    };
    return right(serviceRequest);
  }
}


function toCalculatorInputs(serviceResponse: CalculatorServiceSuccessResponse, avail: AvailableInvestmentStrategies): CalculatorInputs {
  const init = initialCalculatorInputs;
  //const requests: Array<InvestmentRequest> = [];
  const termLengths: Array<TermLength> = [];
  const allocatedStrategies: Array<AllocatedStrategy> = [];

  for (const req of serviceResponse.calculator.investment) {
    const strategies = avail.strategies.filter(s => s.productCode === req.investmentIdentifier);
    if (strategies.length === 1) {
      const strategyCfg = strategies[0];
      const strategyType = strategyCfg.strategyType;
      const buffer = strategyCfg.buffer;
      const invIndex = strategyCfg.index;

      const strategy = new InvestmentStrategy(some(strategyType), some(buffer), some(invIndex));
      const allocatedStrategy = new AllocatedStrategy(strategy, some(req.investmentAllocation), some(parseInt(req.customStartYear)))
      allocatedStrategies.push(allocatedStrategy);
      termLengths.push(strategyCfg.termLength);
      req.investmentAllocation
    }
  }
  const initWithTerm = (termLengths[0])
    ? init.withTerm(termLengths[0])
    : init.withoutTerm();
  const initWithPremium = initWithTerm.withPremiumPayment(serviceResponse.calculator.premium.premiumPaymentAmount)

  return initWithPremium.withStrategyList(new StrategyList(allocatedStrategies));
}

function sortResponseStrategies(response: CalculatorResponse, inputs: CalculatorInputs, config: CalculatorConfig): CalculatorResponse {
  const inputStrategyCodes: Array<string> = [];
  for (let i = 0; i < inputs.strategyList.getStrategyCount(); i++) {
    const strategy = inputs.strategyList.getAllocatedStrategy(i).strategy;
    const cfg = getStrategyConfig(inputs.term, strategy, config.availableInvestmentStrategies);
    const code = pipe(cfg,
      fold(() => 'none',
        (ais) => ais.productCode));

    inputStrategyCodes.push(code);
  }

  const newResponseInvestmentsArray: Array<InvestmentDTO> = [];
  for (const code of inputStrategyCodes) {
    for (const inv of response.investments) {
      if (inv.investmentIdentifier === code) {
        newResponseInvestmentsArray.push(inv);
        break;
      }
    }
  }
  const newResponse: CalculatorResponse = {
    ...response,
    investments: newResponseInvestmentsArray,
  };
  return newResponse;
}


export function toResponse(serviceResponse: CalculatorServiceSuccessResponse, config: CalculatorConfig, optInputs: Option<CalculatorInputs>): Either<Error, CalculatorResponse> {

  //const { steps } = useContext(CalculatorStepsContext);
  const inputs = toCalculatorInputs(serviceResponse, config.availableInvestmentStrategies);


  const inputDtos: Array<CalculatorInputsDTO> = [];
  const avail = config.availableInvestmentStrategies;
  const amount = pipe(inputs.premiumPayment, fold(() => 25000, (premium) => premium)); // HACK TODO: fix this

  const years = pipe(inputs.term,
    fold(() => 1, (t) => t.years)); // TODO: this is a fallback
  for (let i = 0; i < inputs.strategyList.getStrategyCount(); i++) {
    const aStrat = inputs.strategyList.getAllocatedStrategy(i);
    const cfg = getStrategyConfig(inputs.term, aStrat.strategy, avail);
    pipe(cfg,
      fold(() => console.log(`Calculator API: config not found for strategy ${i + 1}`),
        (s) => pipe(aStrat.allocation, fold(() => console.log(`Calculator API: no allocation for strategy ${i + 1}`),
          (allocation) => {
            const percentage = allocation / 100.0;
            const allocationAmount = Math.round(amount * percentage);
            const dto = new CalculatorInputsDTO(s.strategyType.code, allocationAmount, years, s.buffer.percentage, s.index.code, s.productCode)
            inputDtos.push(dto);
          }))));
  }

  const investmentData = serviceResponse.scenarios;
  const allInvestments: Array<InvestmentDTO> = investmentData.map((x: { values: any[]; investmentIdentifier: string }) => {
    const upMarketDTO: ValuesDTO = x.values.find(market => market.illustrationModel === 'UpMarket');
    const downMarketDTO: ValuesDTO = x.values.find(market => market.illustrationModel === 'DownMarket');
    const downMarketLessThanEqualBufferDTO: ValuesDTO = x.values.find(market => market.illustrationModel === 'DownMarketLessThanEqualBuffer');
    const downMarketGreaterThanBufferDTO: ValuesDTO = x.values.find(market => market.illustrationModel === 'DownMarketGreaterThanBuffer');
    const customMarketDTO: ValuesDTO = x.values.find(market => market.illustrationModel === 'Term');
    const mappedInputsDTO = inputDtos.filter(s => s.productCode === x.investmentIdentifier)[0];
    const strategyDTO = new InvestmentDTO(x.investmentIdentifier, downMarketDTO, upMarketDTO, customMarketDTO, mappedInputsDTO, downMarketLessThanEqualBufferDTO, downMarketGreaterThanBufferDTO);
    return strategyDTO;
  });
  const investments = allInvestments.filter(function (inv) { return inv.calculatorInputsDTO ? true : false });

  const sortInputs = pipe(optInputs, fold(() => inputs, (i) => i));

  const scenarios = investmentData as Array<InvestmentScenario>;
  const security = serviceResponse.security;

  const response: CalculatorResponse = { inputs, investments, scenarios, security };
  const sortedResponse = sortResponseStrategies(response, sortInputs, config);
  if (sortedResponse.investments.length === response.investments.length) {
    return right(sortedResponse);
  } else {
    console.log('Calculator API: sorting of responses did not match inputs');
    return right(response);
  }
}

export function toExportResponse(serviceResponse: ExportServiceSuccessResponse): Either<Error, ExportResponse> {
  const response: ExportResponse = { reportURL: serviceResponse.reportURL };
  return right(response);
}

export async function apiCalculatorPost(config: CalculatorConfig, req: CalculatorServiceRequest): Promise<AxiosResponse<any>> {
  const flexGuardCalculatorUrl = config.calculatorUrl;
  if (flexGuardCalculatorUrl) {
    return calculatorApiInstance.post(flexGuardCalculatorUrl, req, {});
  } else {
    console.log("Calculator Error: the calculator service URL was not found in the calculator configuration");
    throw new Error(CALCULATOR_UNAVAILABLE_MESSAGE);
  }
}

export async function apiExportPost(config: CalculatorConfig, request: CalculatorServiceRequest, cancelToken: CancelToken): Promise<AxiosResponse<any>> {
  const flexGuardExportUrl = config.exportUrl;
  if (flexGuardExportUrl) {
    try {
      const axiosConfig: AxiosRequestConfig = { headers: { 'X-PruRequestID': getPruRequestID() }, cancelToken: cancelToken };
      const httpResponse = await defaultApiInstance.post(flexGuardExportUrl, request, axiosConfig);
      return httpResponse;
    } catch (err) {
      console.log(err);
      const errorResponse = axios.isCancel(err)
        ? EXPORT_CANCELLED_MESSAGE : EXPORT_UNAVAILABLE_MESSAGE;
      throw new Error(errorResponse);
    }
  } else {
    console.log("Calculator Error: the export service URL was not found in the calculator configuration");
    throw new Error(EXPORT_UNAVAILABLE_MESSAGE);
  }
}



// Public function
export async function calculate(inputs: CalculatorInputs, config: CalculatorConfig): Promise<Either<Error, CalculatorResponse>> {
  return pipe(calculatorInputsToServiceRequest(inputs, config),
    foldEither(
      (err) => Promise.resolve(left(err)),
      (req) => apiCalculatorPost(config, req).then(
        (response) => {
          const serviceResponse = response.data as CalculatorServiceSuccessResponse;
          return toResponse(serviceResponse, config, some(inputs));
        },
        (error) => { return left(error) }
      )));

}

export function createCancelTokenSource() {
  return axios.CancelToken.source();
}

export async function exportPdf(response: CalculatorResponse, inputs: CalculatorInputs, config: CalculatorConfig, cancelToken: CancelToken): Promise<Either<Error, ExportResponse>> {
  const initialInputsWithExportReport = response.inputs.withExportReport(inputs.exportReport);
  return pipe(calculatorInputsToServiceRequest(initialInputsWithExportReport, config),
    foldEither(
      (err) => Promise.resolve(left(err)),
      (req) => {

        const exportPayload = {
          calculator: req.calculator,
          scenarios: response.scenarios,
          security: response.security
        };
        return apiExportPost(config, exportPayload, cancelToken).then(
          (response) => {
            const serviceResponse = response.data as ExportServiceSuccessResponse;
            return toExportResponse(serviceResponse);
          },
          (error) => { return left(error) }
        );
      }));
}


export function getPDFUrl(config: CalculatorConfig, appId: string, checkSum: string, docNumber: string, productCusip: string): string {
  const appIdClean = appId.trim().replace(/[^\w.-]/g, '');
  const checkSumClean = checkSum.trim().replace(/[^\w\d]/g, '');
  const docNumberClean = docNumber.trim().replace(/[^\d]/g, '');  
  if (!config.exportUrl) {
    console.error(`Cannot fetch PDF; exportUrl is not set`);
  }
  const exportUrl: string = config.exportUrl || '#';
  return config.pdfUrlPattern.replace('${ExportURL}', exportUrl).replace('${ProductCusip}', productCusip).replace('${ApplicationID}', appIdClean).replace('${CheckSum}', checkSumClean).replace('${DocNo}', docNumberClean);
}
